import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete, Visibility } from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { rewriteToCdn } from '../../utils/cdn';
import { getResultGroupCoverUrl } from '../../utils/resultGalleryMedia';
import { normalizeAppProduct, type AppProduct } from '../../utils/deviceMgmtFeatures';
import { userCardEnterSx } from './UserPageShell';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';

const PRO_ACCENT = '#e86a8a';

type GalleryRow = {
  folderId: string;
  createdAt: string;
  viewUrl?: string;
  photos: Array<{ name?: string; url: string; mediaType?: string }>;
  printCount?: number;
};

export interface MachineScopedProPhotosProps {
  machineCode: string;
  /** Admin xem ảnh của user — nếu không có thì dùng API của user hiện tại */
  userId?: string;
  /** Product máy: pro | lite | mono — chọn đúng API gallery */
  appProduct?: AppProduct;
  /** Phần tử cuộn riêng. Không truyền thì cuộn theo trang. */
  scrollableTarget?: string;
}

const MachineScopedProPhotos: React.FC<MachineScopedProPhotosProps> = ({
  machineCode,
  userId,
  appProduct,
  scrollableTarget,
}) => {
  const { pages: t, formatMessage } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const hideDelete = isStaffUser(user);
  const [photoGroups, setPhotoGroups] = useState<GalleryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const limit = 10;
  const code = String(machineCode || '').trim().toUpperCase();
  const ownerUserId = String(userId || '').trim() || undefined;
  const product = normalizeAppProduct(appProduct) || 'pro';

  const fetchPage = useCallback(
    async (pageNumber: number, reset: boolean) => {
      if (!code) return;
      try {
        setLoading(true);
        setLoadError(false);
        const filter = { machineCode: code };
        let response: { data?: GalleryRow[]; pagination?: { totalPages?: number } };

        if (product === 'lite') {
          response = ownerUserId
            ? await api.getAdminUserLitePhotoGroups(ownerUserId, pageNumber, limit, filter)
            : await api.getLitePhotoGroups(pageNumber, limit, filter);
        } else if (product === 'mono') {
          response = ownerUserId
            ? await api.getAdminUserMonoPhotoGroups(ownerUserId, pageNumber, limit, filter)
            : await api.getMonoPhotoGroups(pageNumber, limit, filter);
        } else {
          response = ownerUserId
            ? await api.getUserPhotos(ownerUserId, pageNumber, limit, filter)
            : await api.getPhotoGroups(pageNumber, limit, filter);
        }

        const rows = Array.isArray(response?.data) ? response.data : [];
        const totalPages = Number(response?.pagination?.totalPages) || 0;

        setPhotoGroups((prev) => (reset ? rows : [...prev, ...rows]));
        setHasMore(pageNumber < totalPages);
        setPage(pageNumber);
      } catch (error) {
        console.error('Error loading machine photos:', error);
        setLoadError(true);
        if (reset) setPhotoGroups([]);
        toast.error(t.deviceMgmt.photosLoadError);
      } finally {
        setLoading(false);
      }
    },
    [code, ownerUserId, product, t.deviceMgmt.photosLoadError]
  );

  useEffect(() => {
    setPhotoGroups([]);
    setHasMore(true);
    setPage(1);
    setLoadError(false);
    fetchPage(1, true);
  }, [code, ownerUserId, product]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchPage(page + 1, false);
    }
  };

  const handleOpenGallery = (group: GalleryRow) => {
    if (group.viewUrl) {
      window.open(group.viewUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    toast.error(t.deviceMgmt.photosNoLink);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPhotoId) return;
    try {
      if (product === 'lite') {
        if (ownerUserId) await api.adminDeleteUserLitePhotoGroup(ownerUserId, selectedPhotoId);
        else await api.deleteLitePhotoGroup(selectedPhotoId);
      } else if (product === 'mono') {
        if (ownerUserId) await api.adminDeleteUserMonoPhotoGroup(ownerUserId, selectedPhotoId);
        else await api.deleteMonoPhotoGroup(selectedPhotoId);
      } else if (ownerUserId) {
        await api.adminDeleteUserPhoto(ownerUserId, selectedPhotoId);
      } else {
        await api.deletePhotoGroup(selectedPhotoId);
      }
      setPhotoGroups((prev) => prev.filter((g) => g.folderId !== selectedPhotoId));
      toast.success(t.deviceMgmt.photosDeleteSuccess);
    } catch (error) {
      console.error('Error deleting photo group:', error);
      toast.error(t.deviceMgmt.photosDeleteError);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPhotoId(null);
    }
  };

  const filterChip = (
    <Chip
      label={formatMessage(t.deviceMgmt.photosFilteredBy, { code })}
      size="small"
      sx={{
        mb: 1.5,
        fontWeight: 700,
        bgcolor: 'rgba(232, 106, 138, 0.12)',
        color: PRO_ACCENT,
        border: '1px solid',
        borderColor: 'rgba(232, 106, 138, 0.35)',
      }}
    />
  );

  if (loading && photoGroups.length === 0) {
    return (
      <Box>
        {filterChip}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={180}>
          <CircularProgress size={28} sx={{ color: PRO_ACCENT }} />
        </Box>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        {filterChip}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={5}
          gap={1.5}
          sx={{ backgroundColor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" color="error" fontWeight={700}>
            {t.deviceMgmt.photosLoadError}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => fetchPage(1, true)} sx={{ borderColor: PRO_ACCENT, color: PRO_ACCENT }}>
            {t.common.refresh}
          </Button>
        </Box>
      </Box>
    );
  }

  if (photoGroups.length === 0) {
    return (
      <Box>
        {filterChip}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={5}
          sx={{ backgroundColor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
            {t.deviceMgmt.photosEmptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
            {formatMessage(t.deviceMgmt.photosEmptyHint, { code })}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {filterChip}
      <InfiniteScroll
        dataLength={photoGroups.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} sx={{ color: PRO_ACCENT }} />
          </Box>
        }
        scrollThreshold="200px"
        scrollableTarget={scrollableTarget}
        style={{ overflow: 'hidden' }}
        endMessage={
          <Typography textAlign="center" color="text.secondary" py={1.5} variant="caption">
            {formatMessage(t.deviceMgmt.photosEnd, { code })}
          </Typography>
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: { xs: 1.25, sm: 2 },
            width: '100%',
          }}
        >
          {photoGroups.map((group, index) => {
            const coverUrl = getResultGroupCoverUrl(group.photos);
            return (
              <Card
                key={group.folderId}
                elevation={0}
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  ...userCardEnterSx(index),
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    pt: '75%',
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={coverUrl ? rewriteToCdn(coverUrl) : '/placeholder.png'}
                    alt={`Photo group ${group.folderId}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <CardContent
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 1, sm: 1.25 },
                    '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={group.folderId}
                    sx={{
                      fontSize: { xs: '0.68rem', sm: '0.8rem' },
                      fontFamily: 'ui-monospace, monospace',
                      mb: 0.25,
                    }}
                  >
                    {group.folderId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {format(new Date(group.createdAt), 'dd/MM/yyyy')}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${group.photos.length} ảnh · ${group.printCount || 0} lượt in`}
                    sx={{
                      mt: 0.5,
                      height: 22,
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      bgcolor: alpha('#fff', 0.92),
                      color: PRO_ACCENT,
                      border: `1px solid ${alpha(PRO_ACCENT, 0.35)}`,
                    }}
                  />
                </CardContent>
                <CardActions sx={{ px: { xs: 0.5, sm: 1 }, pt: 0, minHeight: 36 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility sx={{ fontSize: 16 }} />}
                    onClick={() => handleOpenGallery(group)}
                    sx={{ fontSize: '0.75rem', minWidth: 0, color: PRO_ACCENT }}
                  >
                    View
                  </Button>
                  {!hideDelete && (
                  <IconButton
                    onClick={() => {
                      setSelectedPhotoId(group.folderId);
                      setDeleteDialogOpen(true);
                    }}
                    color="error"
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  )}
                </CardActions>
              </Card>
            );
          })}
        </Box>
      </InfiniteScroll>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t.deviceMgmt.photosDeleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t.deviceMgmt.photosDeleteConfirm}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t.common.cancel}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineScopedProPhotos;
